import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
  FormControlLabel,
  Snackbar,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  Search as SearchIcon,
  DragIndicator as DragIndicatorIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Route as RouteIcon,
  Tune as TuneIcon,
  Print as PrintIcon,
  AutoFixHigh as OptimizeIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  SwapHoriz as MoveIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  People as CrewIcon,
  NotificationImportant as ReminderIcon,
  NotificationImportantOutlined as ReminderOutlinedIcon,
  HourglassBottom as HourglassIcon,
  Translate as TranslateIcon,
  AcUnit as SnowIcon,
  WbSunny as SummerIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasMinRole } from '../../utils/roles';
import RouteMap from './RouteMap';
import AdditionalCrewField, { additionalCrewFromTruck, employeeDisplayName } from './AdditionalCrewField';

// Stop position box (TKT-442): type a number to move the stop there
// instead of dragging. Commits on Enter or when the box loses focus.
function StopNumberField({ index, count, onMove, ariaLabel }) {
  const [value, setValue] = useState(String(index + 1));
  useEffect(() => setValue(String(index + 1)), [index]);

  const commit = () => {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n)) {
      setValue(String(index + 1));
      return;
    }
    const target = Math.min(Math.max(n, 1), count) - 1;
    if (target === index) setValue(String(index + 1));
    else onMove(target);
  };

  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();
        } else if (e.key === 'Escape') {
          setValue(String(index + 1));
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onDragStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      inputProps={{
        inputMode: 'numeric',
        'aria-label': ariaLabel,
        style: { textAlign: 'center', fontWeight: 700, padding: '4px 2px', width: 34 },
      }}
      sx={{ flexShrink: 0 }}
    />
  );
}

const DAYS_OF_WEEK = [
  { value: 1, key: 'monday' },
  { value: 2, key: 'tuesday' },
  { value: 3, key: 'wednesday' },
  { value: 4, key: 'thursday' },
  { value: 5, key: 'friday' },
  { value: 6, key: 'saturday' },
  { value: 7, key: 'sunday' },
];

// TKT-443: each truck's snow route is one list for the whole season (PL
// Hub's Winter Truck Route), stored as day 0. It gets its own grid column;
// crews open it from the Snow switch in Crew Mode.
const SNOW_LIST_DAY = 0;
const SLOT_COLUMNS = [...DAYS_OF_WEEK, { value: SNOW_LIST_DAY, key: 'snowList' }];

/**
 * Unified Route Setup page. Replaces the older RouteManagement,
 * RouteEditor, SimpleRouteSetup, RouteSetupWizard, ManageJobSites.
 *
 * Mental model (matches legacy): a route stop is a job_sites row tied to
 * a (day_of_week, truck, priority) slot. The weekly template IS the schedule.
 *
 * Flow:
 *  1) Pick a (Day, Truck) slot from the grid.
 *  2) Add customers (from approved quotes) to that slot.
 *  3) Reorder via drag-drop. Optionally optimize with Mapbox.
 */
const RouteSetup = () => {
  // TKT-482: optimizing a route is Admin / Super Admin only (Naomi, October 28, 2026).
  const { user: currentUser } = useAuth();
  const canOptimize = hasMinRole(currentUser, 'admin');
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ------------- Global data -------------
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [savingSeasonId, setSavingSeasonId] = useState(null);

  // ------------- Selection -------------
  const [selectedDay, setSelectedDay] = useState(null);   // 1..7
  const [selectedTruckId, setSelectedTruckId] = useState(null);

  // ------------- Slot data -------------
  const [slotJobSites, setSlotJobSites] = useState([]); // ordered by priority
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotCounts, setSlotCounts] = useState({}); // key = `${day}:${truckId}` -> count
  const [slotOpen, setSlotOpen] = useState(false);
  const [routeGeometry, setRouteGeometry] = useState(null); // GeoJSON LineString
  const [depotCoords, setDepotCoords] = useState(null);     // { latitude, longitude, name }
  const [geocodingSlot, setGeocodingSlot] = useState(false);
  // Ref mirrors the current selection so async callbacks can check whether
  // a stale response still matches what the user is looking at, without
  // being foiled by React's async state updates.
  const currentSlotRef = useRef({ day: null, truckId: null });

  // ------------- Add Customers dialog -------------
  const [addCustomersOpen, setAddCustomersOpen] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  // TKT-424: the season of new stops must be picked each time Add Customers
  // opens. A switch that defaulted to summer let winter test stops go in as
  // summer stops, so the crew saw the summer checklist.
  const [stopSeason, setStopSeason] = useState(''); // '' | 'summer' | 'snow'
  const isSnow = stopSeason === 'snow';
  const [adding, setAdding] = useState({}); // customer_id -> bool

  // ------------- Quote picker (opened when customer has 2+ approved quotes) -------------
  const [quotePickerOpen, setQuotePickerOpen] = useState(false);
  const [quotePickerCustomer, setQuotePickerCustomer] = useState(null); // full customer row
  const [quotePickerLoadingId, setQuotePickerLoadingId] = useState(null); // quote id currently being added

  // ------------- Optimize dialog (Phase 3b wires UI) -------------
  // Move-to-slot dialog state (mirrors legacy's "Select Location" popup:
  // moves one stop to a different day/truck without losing its history).
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // full job_site row
  const [moveDay, setMoveDay] = useState(null);
  const [moveTruckId, setMoveTruckId] = useState(null);
  const [moving, setMoving] = useState(false);

  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [optimizePreview, setOptimizePreview] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [applyingOptimize, setApplyingOptimize] = useState(false);
  const [depotAddress, setDepotAddress] = useState('');
  const [avoidTolls, setAvoidTolls] = useState(true);

  // ------------- Crew dialog (edits truck.current_driver/passenger_user_id) -------------
  // Legacy parity: there's no per-day or per-job crew concept. The crew is a
  // property of the truck. Editing it here updates the truck for all days it runs.
  const [crewOpen, setCrewOpen] = useState(false);
  const [crewDriverId, setCrewDriverId] = useState('');
  const [crewPassengerId, setCrewPassengerId] = useState('');
  const [crewExtras, setCrewExtras] = useState([]);
  const [savingCrew, setSavingCrew] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // ------------- Urgent Reminder edit dialog (FB-004) -------------
  // Office-controlled quote reminder that pops up on the crew's stop card
  // and gates clock-in. Route Setup can edit it as a shortcut, but the
  // backend persists it on the linked quote and syncs all jobs for that quote.
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null); // full job_site row
  const [reminderText, setReminderText] = useState('');
  // TKT-282: kept separate from reminderText so translating no longer
  // overwrites the English original - crews see the reminder in their own
  // language (with English fallback), not whichever language it was last
  // translated to.
  const [reminderTextEs, setReminderTextEs] = useState('');
  const [savingReminder, setSavingReminder] = useState(false);
  const [translatingReminder, setTranslatingReminder] = useState(false);

  // ------------- Time allocation sync dialog (FB-005) -------------
  // Time is quote-derived (legacy parity), not typed into Route Setup.
  // The dialog explains the source and lets dispatch refresh the cached
  // job_sites.allocated_minutes after quote changes.
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeTarget, setTimeTarget] = useState(null); // full job_site row
  const [savingTime, setSavingTime] = useState(false);

  // ------------- Remove / skip dialog -------------
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);

  // ------------- Reorder state (drag/drop) -------------
  const [dragIndex, setDragIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  // Toronto date: after 8 PM the UTC date is already tomorrow (TKT-445).
  const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const todayDow = (() => {
    const day = new Date().getDay();
    return day === 0 ? 7 : day;
  })();

  const slotDayLabel = (day) => {
    if (day === SNOW_LIST_DAY) return t('routeSetup.snowListColumn');
    const column = DAYS_OF_WEEK.find((d) => d.value === day);
    return column ? t(`days.${column.key}`) : '';
  };
  const isSnowListSlot = selectedDay === SNOW_LIST_DAY;

  // ===================================================================
  // INITIAL LOAD
  // ===================================================================

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [trucksRes, depotRes] = await Promise.all([
          makeApiCall('/api/routes/trucks'),
          makeApiCall('/api/routes/depot'),
        ]);
        if (!trucksRes.ok) throw new Error('Failed to load trucks');
        const trucksData = await trucksRes.json();
        setTrucks((trucksData.trucks || []).filter(tr => tr.is_active !== false));
        if (depotRes.ok) {
          const depotData = await depotRes.json();
          if (depotData?.address) setDepotAddress(depotData.address);
        }
      } catch (err) {
        console.error(err);
        setError(t('routeSetup.failedToLoadTrucks'));
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute cell counts whenever trucks load
  useEffect(() => {
    if (trucks.length === 0) return;
    loadAllSlotCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trucks]);

  const loadAllSlotCounts = async () => {
    try {
      const response = await makeApiCall('/api/routes/job-sites?is_active=true');
      if (!response.ok) return;
      const data = await response.json();
      const counts = {};
      (data.job_sites || []).forEach((js) => {
        const day = js.is_custom ? js.custom_day_of_week : js.day_of_week;
        const truck = js.is_custom ? js.custom_truck_id : js.truck_id;
        if (day == null || !truck) return;
        const key = `${day}:${truck}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      setSlotCounts(counts);
    } catch (err) {
      console.error('Failed to load slot counts', err);
    }
  };

  // ===================================================================
  // SLOT SELECTION
  // ===================================================================

  const selectSlot = (day, truckId) => {
    currentSlotRef.current = { day, truckId };
    setSelectedDay(day);
    setSelectedTruckId(truckId);
    setSlotOpen(true);
    setRouteGeometry(null); // previous slot's polyline is no longer relevant
    loadSlot(day, truckId);
    // Kick off a background geocode backfill so the map has markers.
    backfillSlotCoords(day, truckId);
  };

  const closeSlot = () => {
    setSlotOpen(false);
    setRouteGeometry(null);
    currentSlotRef.current = { day: null, truckId: null };
    // Leave selectedDay/TruckId set so a quick re-open shows the same slot.
  };

  // Refresh the trucks list so the slot modal picks up crew changes after a
  // "Assign Crew" save without requiring a full page reload.
  const refreshTrucks = async () => {
    try {
      const response = await makeApiCall('/api/routes/trucks');
      if (response.ok) {
        const data = await response.json();
        setTrucks((data.trucks || []).filter((tr) => tr.is_active !== false));
      }
    } catch (err) {
      console.error('Failed to refresh trucks', err);
    }
  };

  const openCrewDialog = async () => {
    if (!selectedTruck) return;
    setCrewDriverId(selectedTruck.current_driver_user_id || '');
    setCrewPassengerId(selectedTruck.current_passenger_user_id || '');
    setCrewExtras(additionalCrewFromTruck(selectedTruck));
    setCrewOpen(true);
    if (availableEmployees.length === 0 && !loadingEmployees) {
      try {
        setLoadingEmployees(true);
        const response = await makeApiCall('/api/routes/lookups/available-employees?include_subcontractors=true');
        if (response.ok) {
          const data = await response.json();
          setAvailableEmployees(data.available_employees || []);
        }
      } catch (err) {
        console.error('Failed to load employees', err);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  const saveCrew = async () => {
    if (!selectedTruckId) return;
    try {
      setSavingCrew(true);
      const response = await makeApiCall(`/api/routes/trucks/${selectedTruckId}/driver`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_driver_user_id: crewDriverId || null,
          current_passenger_user_id: crewPassengerId || null,
          additional_crew_user_ids: crewExtras.map((emp) => emp.id),
        }),
      });
      if (!response.ok) throw new Error('Failed to save crew');
      await refreshTrucks();
      setCrewOpen(false);
      setSuccessMsg(t('routeSetup.crewSaved'));
    } catch (err) {
      console.error(err);
      setError(t('routeSetup.failedToSaveCrew'));
    } finally {
      setSavingCrew(false);
    }
  };

  const backfillSlotCoords = async (day, truckId) => {
    setGeocodingSlot(true);
    try {
      const res = await makeApiCall('/api/routes/geocode-slot', {
        method: 'POST',
        body: JSON.stringify({ day_of_week: day, truck_id: truckId }),
      });
      if (!res.ok) {
        // Surface the failure so the user knows the map may be missing
        // markers and addresses for new stops. Not fatal — the slot is
        // still editable — but they should know so they can retry.
        const errData = await res.json().catch(() => ({}));
        console.warn('geocode-slot failed:', errData.error || res.status);
        setError(errData.error || t('routeSetup.geocodeFailed'));
        return;
      }
      const data = await res.json();
      // Always reload the slot after the backfill finishes so the map gets
      // the latest coordinates. Cheap no-op when geocoded=0.
      if (data) {
        await loadSlot(day, truckId);
      }
    } catch (err) {
      console.warn('geocode-slot backfill failed', err);
      setError(err.message || t('routeSetup.geocodeFailed'));
    } finally {
      setGeocodingSlot(false);
      // After coords are settled, draw the current-order polyline so the
      // user sees a route on open (not just after optimize).
      loadCurrentRouteGeometry(day, truckId);
    }
  };

  // Fetch the current-order polyline from the backend and stash it for the
  // map. Runs on slot open and after any change that alters stop order.
  const loadCurrentRouteGeometry = async (day, truckId) => {
    try {
      const params = new URLSearchParams({
        day_of_week: String(day),
        truck_id: String(truckId),
      });
      const res = await makeApiCall(`/api/routes/route-geometry?${params.toString()}`);
      if (!res.ok) return;
      const data = await res.json();
      // Only apply if the user is still looking at this slot. The ref
      // (vs. state) avoids false negatives from React's async updates.
      const current = currentSlotRef.current;
      if (current.day === day && current.truckId === truckId) {
        if (data?.geometry) setRouteGeometry(data.geometry);
        if (data?.depot) setDepotCoords(data.depot);
      }
    } catch (err) {
      // Not fatal - map will just render without a polyline.
      console.warn('route-geometry fetch failed', err);
    }
  };

  const loadSlot = async (day, truckId) => {
    try {
      setSlotLoading(true);
      const response = await makeApiCall(
        `/api/routes/job-sites?day_of_week=${day}&truck_id=${truckId}&is_active=true&scheduled_date=${todayIso}`
      );
      if (!response.ok) throw new Error('Failed to load slot');
      const data = await response.json();
      const sorted = (data.job_sites || []).slice().sort(
        (a, b) => (a.priority || 0) - (b.priority || 0)
      );
      setSlotJobSites(sorted);
    } catch (err) {
      console.error(err);
      setError(t('routeSetup.failedToLoadSlot'));
    } finally {
      setSlotLoading(false);
    }
  };

  // ===================================================================
  // ADD CUSTOMERS
  // ===================================================================

  const openAddCustomers = () => {
    setCustomerSearch('');
    setServiceTypeFilter('');
    // The column decides the season: weekdays hold summer stops, the Snow
    // column holds the season snow list.
    setStopSeason(selectedDay === SNOW_LIST_DAY ? 'snow' : 'summer');
    setAddCustomersOpen(true);
    loadAvailableCustomers();
  };

  const loadAvailableCustomers = async () => {
    try {
      setAvailableLoading(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        search: customerSearch,
        hasQuote: 'true',
        quoteStatus: 'approved',
        excludeExistingJobSites: 'true',
      });
      if (selectedDay != null && selectedTruckId) {
        params.append('day_of_week', String(selectedDay));
        params.append('truck_id', String(selectedTruckId));
      }
      // TKT-467: snow stops can repeat, so the list keeps every property.
      if (isSnow) params.append('is_snow', 'true');
      if (serviceTypeFilter) params.append('serviceType', serviceTypeFilter);
      const response = await makeApiCall(
        `/api/routes/available-customers?${params.toString()}`
      );
      if (!response.ok) throw new Error('Failed to load customers');
      const data = await response.json();
      setAvailableCustomers(data.customers || []);
    } catch (err) {
      console.error(err);
      setError(t('routeSetup.failedToLoadCustomers'));
    } finally {
      setAvailableLoading(false);
    }
  };

  // Debounced re-search
  useEffect(() => {
    if (!addCustomersOpen) return;
    const timer = setTimeout(() => loadAvailableCustomers(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch, serviceTypeFilter, addCustomersOpen, isSnow]);

  // Actually POST the job_site once we know which quote to use. A route
  // stop is a contract (quote) + scheduling info — a customer alone isn't
  // enough to tell the crew what to do, so quote_id is mandatory.
  const createJobSiteForQuote = async (customer, quote) => {
    if (selectedDay == null || !selectedTruckId || !stopSeason) return false;
    if (!customer?.id || !quote?.id) return false;
    const customerId = customer.id;
    const quoteId = quote.id;
    try {
      setAdding((a) => ({ ...a, [customerId]: true }));
      setQuotePickerLoadingId(quoteId);
      const payload = {
        customer_id: customerId,
        quote_id: quoteId,
        day_of_week: selectedDay,
        truck_id: selectedTruckId,
        lawn_work_time: quote.lawn_work_time || null,
        gardening_work_time: quote.gardening_work_time || null,
        lawn_weekly_price: quote.lawn_weekly_price || null,
        gardening_weekly_price: quote.gardening_weekly_price || null,
        is_snow_job: isSnow,
      };
      const response = await makeApiCall('/api/routes/job-sites', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to add');
      }
      await loadSlot(selectedDay, selectedTruckId);
      await loadAllSlotCounts();
      // New stop => recompute the polyline + geocode any missing coords for it.
      backfillSlotCoords(selectedDay, selectedTruckId);
      // Refresh the picker list so a customer whose only remaining quote
      // was just scheduled disappears / updates in place (summer only; snow
      // stops stay listed so they can be added again).
      await loadAvailableCustomers();
      setSuccessMsg(t('routeSetup.customerAdded'));
      return true;
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToAdd'));
      return false;
    } finally {
      setAdding((a) => {
        const next = { ...a };
        delete next[customerId];
        return next;
      });
      setQuotePickerLoadingId(null);
    }
  };

  // Click "Add" in the customer list. Three outcomes based on how many
  // approved quotes the customer has that aren't already in this route slot:
  //  - 0: shouldn't happen (the filter excludes them), but we guard anyway.
  //  - 1: add directly.
  //  - 2+: open the quote-picker modal so the user chooses which contract.
  const addCustomerToSlot = async (customer) => {
    if (selectedDay == null || !selectedTruckId || !stopSeason) return;
    const quotes = Array.isArray(customer.available_quotes)
      ? customer.available_quotes
      : [];
    if (quotes.length === 0) {
      setError(t('routeSetup.noAvailableQuotes'));
      return;
    }
    if (quotes.length === 1) {
      await createJobSiteForQuote(customer, quotes[0]);
      return;
    }
    // Multiple quotes -> let the user pick.
    setQuotePickerCustomer(customer);
    setQuotePickerOpen(true);
  };

  const confirmQuotePick = async (quote) => {
    const customer = quotePickerCustomer;
    if (!customer) return;
    const ok = await createJobSiteForQuote(customer, quote);
    if (ok) {
      setQuotePickerOpen(false);
      setQuotePickerCustomer(null);
    }
  };

  // ===================================================================
  // REORDER (drag & drop + up/down arrows)
  // ===================================================================

  const reorderLocal = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = slotJobSites.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setSlotJobSites(next);
    persistReorder(next);
  };

  const persistReorder = async (orderedList) => {
    if (selectedDay == null || !selectedTruckId) return;
    try {
      setSaving(true);
      const orderedIds = orderedList.map((js) => js.id);
      const response = await makeApiCall('/api/routes/reorder', {
        method: 'POST',
        body: JSON.stringify({
          day_of_week: selectedDay,
          truck_id: selectedTruckId,
          ordered_job_site_ids: orderedIds,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Reorder failed');
      }
      // Optimistically update priorities in local state
      setSlotJobSites((prev) =>
        prev.map((js, idx) => ({ ...js, priority: idx + 1 }))
      );
      // Redraw the polyline to match the new stop order.
      loadCurrentRouteGeometry(selectedDay, selectedTruckId);
    } catch (err) {
      console.error(err);
      setError(t('routeSetup.failedToReorder'));
      // Reload to be safe
      loadSlot(selectedDay, selectedTruckId);
    } finally {
      setSaving(false);
    }
  };

  const formatAllocatedMinutes = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return t('routeSetup.minutesWithHoursClock', {
      minutes: total,
      clock: `${hours}:${String(mins).padStart(2, '0')}`,
    });
  };

  const getAllocationBreakdownLabel = (item) => {
    const key = item?.key || item?.service_type || 'unknown';
    const breakdownKey = `routeSetup.breakdownLabels.${key}`;
    const translatedBreakdown = t(breakdownKey);
    if (translatedBreakdown !== breakdownKey) return translatedBreakdown;

    const serviceKey = `serviceNames.${key}`;
    const translatedService = t(serviceKey);
    if (translatedService !== serviceKey) return translatedService;

    return key.replace(/([A-Z])/g, ' $1').replace(/[_-]+/g, ' ').trim();
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (dragIndex === null) return;
    reorderLocal(dragIndex, idx);
    setDragIndex(null);
  };

  const moveUp = (idx) => reorderLocal(idx, Math.max(0, idx - 1));
  const moveDown = (idx) => reorderLocal(idx, Math.min(slotJobSites.length - 1, idx + 1));

  // ===================================================================
  // MOVE TO ANOTHER SLOT (legacy "Select Location" popup)
  // ===================================================================

  const openMove = (stop) => {
    setMoveTarget(stop);
    setMoveDay(selectedDay);
    setMoveTruckId(selectedTruckId);
    setMoveOpen(true);
  };

  const closeMove = () => {
    setMoveOpen(false);
    setMoveTarget(null);
  };

  const applyMove = async () => {
    if (!moveTarget || moveDay == null || !moveTruckId) return;
    // No-op move (same slot) - just close.
    if (moveDay === selectedDay && moveTruckId === selectedTruckId) {
      closeMove();
      return;
    }
    try {
      setMoving(true);
      // Backend PUT /job-sites/:id accepts partial updates including
      // day_of_week, truck_id, priority. We append to the end of the target
      // slot so the operator can fine-tune order there.
      const response = await makeApiCall(`/api/routes/job-sites/${moveTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          day_of_week: moveDay,
          truck_id: moveTruckId,
          priority: null, // server will renumber to MAX+1 in target slot
          drive_time_mins: moveTarget.drive_time_mins,
          lawn_work_time: moveTarget.lawn_work_time,
          gardening_work_time: moveTarget.gardening_work_time,
          lawn_weekly_price: moveTarget.lawn_weekly_price,
          gardening_weekly_price: moveTarget.gardening_weekly_price,
          is_custom: moveTarget.is_custom,
          custom_day_of_week: moveTarget.custom_day_of_week,
          custom_truck_id: moveTarget.custom_truck_id,
          custom_priority: moveTarget.custom_priority,
          custom_time: moveTarget.custom_time,
          is_active: true,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Move failed');
      }
      setSuccessMsg(t('routeSetup.moveSuccess'));
      closeMove();
      // Refresh the current slot (stop will now be gone) and the counts grid.
      await loadSlot(selectedDay, selectedTruckId);
      await loadAllSlotCounts();
      loadCurrentRouteGeometry(selectedDay, selectedTruckId);
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.moveFailed'));
    } finally {
      setMoving(false);
    }
  };

  // ===================================================================
  // REMOVE STOP
  // ===================================================================

  const openRemoveStop = (jobSite) => {
    setRemoveTarget(jobSite);
    setRemoveReason('');
    setRemoveOpen(true);
  };

  const closeRemoveStop = (force = false) => {
    if (removing && !force) return;
    setRemoveOpen(false);
    setRemoveTarget(null);
    setRemoveReason('');
  };

  const refreshAfterRemovalChange = async () => {
    await loadSlot(selectedDay, selectedTruckId);
    await loadAllSlotCounts();
    loadCurrentRouteGeometry(selectedDay, selectedTruckId);
  };

  const skipStopToday = async () => {
    if (!removeTarget?.id) return;
    try {
      setRemoving(true);
      const response = await makeApiCall(
        `/api/routes/job-sites/${removeTarget.id}/skip-today`,
        {
          method: 'POST',
          body: JSON.stringify({
            scheduled_date: todayIso,
            reason: removeReason.trim() || undefined,
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('routeSetup.failedToSkipToday'));
      }
      await refreshAfterRemovalChange();
      setSuccessMsg(t('routeSetup.stopSkippedToday'));
      closeRemoveStop(true);
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToSkipToday'));
    } finally {
      setRemoving(false);
    }
  };

  const removeStopFromFutureRoute = async () => {
    if (!removeTarget?.id) return;
    try {
      setRemoving(true);
      const response = await makeApiCall(
        `/api/routes/job-sites/${removeTarget.id}/remove-future-route`,
        {
          method: 'POST',
          body: JSON.stringify({ scheduled_date: todayIso }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to remove');
      }
      await refreshAfterRemovalChange();
      setSuccessMsg(t('routeSetup.stopRemoved'));
      closeRemoveStop(true);
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToRemove'));
    } finally {
      setRemoving(false);
    }
  };

  const skipTodayAndRemoveFutureRoute = async () => {
    if (!removeTarget?.id) return;
    const targetId = removeTarget.id;
    try {
      setRemoving(true);
      const skipResponse = await makeApiCall(
        `/api/routes/job-sites/${targetId}/skip-today`,
        {
          method: 'POST',
          body: JSON.stringify({
            scheduled_date: todayIso,
            reason: removeReason.trim() || undefined,
          }),
        }
      );
      if (!skipResponse.ok) {
        const data = await skipResponse.json().catch(() => ({}));
        throw new Error(data.error || t('routeSetup.failedToSkipToday'));
      }
      const removeResponse = await makeApiCall(
        `/api/routes/job-sites/${targetId}/remove-future-route`,
        {
          method: 'POST',
          body: JSON.stringify({ scheduled_date: todayIso }),
        }
      );
      if (!removeResponse.ok) {
        const data = await removeResponse.json().catch(() => ({}));
        throw new Error(data.error || t('routeSetup.failedToRemove'));
      }
      await refreshAfterRemovalChange();
      setSuccessMsg(t('routeSetup.stopSkippedAndRemoved'));
      closeRemoveStop(true);
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToRemove'));
    } finally {
      setRemoving(false);
    }
  };

  const canSkipToday = selectedDay === todayDow;

  const isSkippedToday = (jobSite) => !!jobSite?.is_cancelled;

  const removeTargetTitle = removeTarget
    ? (removeTarget.company_name
      || `${removeTarget.first_name || ''} ${removeTarget.last_name || ''}`.trim()
      || t('routeSetup.unknownCustomer'))
    : '';

  const renderRemoveStopDialog = () => (
    <Dialog open={removeOpen} onClose={() => closeRemoveStop()} fullWidth maxWidth="sm">
      <DialogTitle>{t('routeSetup.removeStopDialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            {t('routeSetup.removeStopDialogHelp', { title: removeTargetTitle })}
          </Alert>
          {canSkipToday ? (
            <TextField
              label={t('routeSetup.skipReasonLabel')}
              value={removeReason}
              onChange={(event) => setRemoveReason(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              inputProps={{
                'aria-label': t('routeSetup.skipReasonAria'),
              }}
            />
          ) : (
            <Alert severity="warning">
              {t('routeSetup.skipOnlyTodayUnavailable')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => closeRemoveStop()} disabled={removing}>
          {t('common.cancel')}
        </Button>
        {canSkipToday && (
          <Button
            variant="outlined"
            onClick={skipStopToday}
            disabled={removing || isSkippedToday(removeTarget)}
            aria-label={t('routeSetup.skipOnlyToday')}
          >
            {t('routeSetup.skipOnlyToday')}
          </Button>
        )}
        <Button
          variant="outlined"
          color="error"
          onClick={removeStopFromFutureRoute}
          disabled={removing}
          aria-label={t('routeSetup.removeFutureOnly')}
        >
          {t('routeSetup.removeFutureOnly')}
        </Button>
        {canSkipToday && (
          <Button
            variant="contained"
            color="error"
            onClick={skipTodayAndRemoveFutureRoute}
            disabled={removing}
            aria-label={t('routeSetup.skipTodayAndRemoveFuture')}
          >
            {removing ? <CircularProgress size={18} color="inherit" /> : t('routeSetup.skipTodayAndRemoveFuture')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // ===================================================================
  // OPTIMIZE WITH MAPBOX (preview modal, Phase 3b)
  // ===================================================================

  const openOptimize = () => {
    if (slotJobSites.length < 2) {
      setError(t('routeSetup.optimizeNeedsAtLeastTwoStops'));
      return;
    }
    setOptimizePreview(null);
    setOptimizeOpen(true);
  };

  const runOptimize = async () => {
    try {
      setOptimizing(true);
      setOptimizePreview(null);
      const response = await makeApiCall('/api/routes/optimize', {
        method: 'POST',
        body: JSON.stringify({
          day_of_week: selectedDay,
          truck_id: selectedTruckId,
          depot_address: depotAddress || undefined,
          avoid_tolls: avoidTolls,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Optimize failed');
      }
      const data = await response.json();
      setOptimizePreview(data);
      // Stash the geometry so the map can show the proposed polyline while
      // the preview modal is open.
      if (data?.geometry) setRouteGeometry(data.geometry);
      // The backend response doesn't ship depot coords, but the polyline
      // implicitly starts/ends at the depot, so the map already shows it.
      // (We keep depotCoords null unless we've explicitly geocoded it.)
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToOptimize'));
    } finally {
      setOptimizing(false);
    }
  };

  const applyOptimize = async () => {
    if (!optimizePreview?.optimized_order) return;
    try {
      setApplyingOptimize(true);
      const newOrder = optimizePreview.optimized_order
        .map((id) => slotJobSites.find((js) => js.id === id))
        .filter(Boolean);
      setSlotJobSites(newOrder);
      await persistReorder(newOrder);
      // Keep geometry so the slot map shows the applied route.
      setOptimizeOpen(false);
      setSuccessMsg(t('routeSetup.optimizeApplied'));
    } catch (err) {
      console.error(err);
      setError(t('routeSetup.failedToApplyOptimize'));
    } finally {
      setApplyingOptimize(false);
    }
  };

  // ===================================================================
  // RENDER HELPERS
  // ===================================================================

  const stickyHeadSx = {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
  };

  const renderGrid = () => (
    <Paper sx={{ p: 2, mb: 3 }} elevation={2} data-demo-target="route-setup-grid">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <TuneIcon color="primary" />
        <Typography variant="h6">{t('routeSetup.pickSlotTitle')}</Typography>
        <Tooltip title={t('routeSetup.pickSlotHelp')}>
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('routeSetup.pickSlotSubtitle')}
      </Typography>

      {/* The grid scrolls inside its own box so the day-of-week row can stay
          pinned at the top while scrolling a long truck list (TKT-442). */}
      <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
        <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 640 }}>
          <thead>
            <tr>
              <Box component="th" sx={{ ...stickyHeadSx, textAlign: 'left', p: 1 }}>
                {t('routeSetup.truck')}
              </Box>
              {SLOT_COLUMNS.map((d) => (
                <Box
                  component="th"
                  key={d.value}
                  sx={{ ...stickyHeadSx, textAlign: 'center', p: 1 }}
                >
                  {d.value === SNOW_LIST_DAY ? (
                    <Tooltip title={t('routeSetup.snowListColumnHelp')}>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <SnowIcon fontSize="small" color="info" />
                        <span>{slotDayLabel(d.value)}</span>
                      </Stack>
                    </Tooltip>
                  ) : slotDayLabel(d.value)}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {trucks.map((tr, truckIdx) => (
              <tr key={tr.id}>
                <Box component="td" sx={{ p: 1, fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TruckIcon fontSize="small" color="action" />
                    <span>{tr.name}</span>
                  </Stack>
                </Box>
                {SLOT_COLUMNS.map((d, dayIdx) => {
                  const key = `${d.value}:${tr.id}`;
                  const count = slotCounts[key] || 0;
                  const isSelected = selectedDay === d.value && selectedTruckId === tr.id;
                  // Pick a deterministic "first" slot the demo can click.
                  // We want a slot that actually has scheduled stops so the
                  // dialog opens with content; fall back to the very first
                  // cell if nothing has stops yet.
                  const isFirstSlot = truckIdx === 0 && dayIdx === 0;
                  const isFirstNonEmpty = count > 0
                    && !Object.entries(slotCounts).some(([k, c]) => {
                      if (c <= 0) return false;
                      const [otherDay, otherTruck] = k.split(':');
                      const otherTruckIdx = trucks.findIndex((x) => String(x.id) === otherTruck);
                      const otherDayIdx = SLOT_COLUMNS.findIndex((x) => String(x.value) === otherDay);
                      if (otherTruckIdx < 0 || otherDayIdx < 0) return false;
                      if (otherTruckIdx < truckIdx) return true;
                      if (otherTruckIdx === truckIdx && otherDayIdx < dayIdx) return true;
                      return false;
                    });
                  return (
                    <Box
                      component="td"
                      key={d.value}
                      sx={{
                        p: 0.75,
                        textAlign: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        variant={isSelected ? 'contained' : 'outlined'}
                        size="small"
                        fullWidth
                        onClick={() => selectSlot(d.value, tr.id)}
                        aria-label={t('routeSetup.selectSlotAria', {
                          truck: tr.name,
                          day: slotDayLabel(d.value),
                        })}
                        color={d.value === SNOW_LIST_DAY ? 'info' : 'primary'}
                        data-demo-target={isFirstNonEmpty ? 'route-setup-slot-demo' : (isFirstSlot ? 'route-setup-slot-first' : undefined)}
                        sx={{ minWidth: 60, py: 1 }}
                      >
                        {count > 0 ? count : '\u2013'}
                      </Button>
                    </Box>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Paper>
  );

  const selectedTruck = trucks.find((tr) => tr.id === selectedTruckId);
  const selectedDayLabel = selectedDay != null ? slotDayLabel(selectedDay) : '';

  // Map stops derived for the RouteMap. Only stops with cached coords render
  // as markers; others are skipped (RouteMap shows a "Geocoding..." banner).
  const mapStops = slotJobSites.map((js, idx) => {
    const title = js.company_name
      || `${js.first_name || ''} ${js.last_name || ''}`.trim()
      || t('routeSetup.unknownCustomer');
    return {
      id: js.id,
      priority: idx + 1,
      latitude: js.latitude,
      longitude: js.longitude,
      title,
      subtitle: [js.street_address, js.city].filter(Boolean).join(', '),
    };
  });

  const renderSlotDialog = () => (
    <Dialog
      open={slotOpen}
      onClose={closeSlot}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="route-setup-slot-dialog-title"
      PaperProps={{ 'data-demo-target': 'route-setup-slot-dialog' }}
    >
      <DialogTitle id="route-setup-slot-dialog-title" sx={{ pr: 6 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
          <Box>
            <Typography variant="h6" component="div">
              {selectedDayLabel} · {selectedTruck?.name || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {isSnowListSlot ? t('routeSetup.snowListSlotHelp') : t('routeSetup.stopsOrderedByPriority')}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
              <strong>{t('routeSetup.crew')}:</strong>{' '}
              {selectedTruck?.driver_first_name || selectedTruck?.driver_last_name
                ? `${selectedTruck.driver_first_name || ''} ${selectedTruck.driver_last_name || ''}`.trim()
                : t('routeSetup.noDriver')}
              {' · '}
              {selectedTruck?.passenger_first_name || selectedTruck?.passenger_last_name
                ? `${selectedTruck.passenger_first_name || ''} ${selectedTruck.passenger_last_name || ''}`.trim()
                : t('routeSetup.noPassenger')}
              {additionalCrewFromTruck(selectedTruck).length > 0 && (
                <>
                  {' · '}
                  {t('routeSetup.additionalCrewInline', {
                    names: additionalCrewFromTruck(selectedTruck).map(employeeDisplayName).join(', '),
                  })}
                </>
              )}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openAddCustomers}
              aria-label={t('routeSetup.addCustomers')}
              data-demo-target="route-setup-slot-add-customers"
            >
              {t('routeSetup.addCustomers')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CrewIcon />}
              onClick={openCrewDialog}
              aria-label={t('routeSetup.assignCrew')}
              data-demo-target="route-setup-slot-assign-crew"
            >
              {t('routeSetup.assignCrew')}
            </Button>
            {canOptimize && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<OptimizeIcon />}
              onClick={openOptimize}
              disabled={slotJobSites.length < 2}
              aria-label={t('routeSetup.optimize')}
              data-demo-target="route-setup-slot-optimize"
            >
              {t('routeSetup.optimize')}
            </Button>
            )}
            <Tooltip title={t('routeSetup.printTooltip')}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() =>
                    window.open(
                      `/schedule/print?day_of_week=${selectedDay}&truck_id=${selectedTruckId}`,
                      '_blank'
                    )
                  }
                  disabled={slotJobSites.length === 0 || isSnowListSlot}
                  aria-label={t('common.print')}
                >
                  {t('common.print')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <IconButton
          aria-label={t('common.close')}
          onClick={closeSlot}
          data-demo-target="route-setup-slot-close"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {(saving || slotLoading) && <LinearProgress sx={{ mb: 1 }} />}

        <Grid container spacing={2}>
          {/* Stops list on the left */}
          <Grid size={{ xs: 12, md: 6 }}>
            {slotJobSites.length === 0 && !slotLoading ? (
              <Alert severity="info">
                {t('routeSetup.emptySlot')}
              </Alert>
            ) : (
              <List dense disablePadding>
                {slotJobSites.map((js, idx) => {
                  const title = js.company_name
                    ? js.company_name
                    : `${js.first_name || ''} ${js.last_name || ''}`.trim() || t('routeSetup.unknownCustomer');
                  const subtitle = [js.street_address, js.city, js.province, js.postal_code]
                    .filter(Boolean).join(', ');
                  const totalWorkTime = Number(js.allocated_minutes || 0)
                    || (Number(js.lawn_work_time) || 0) + (Number(js.gardening_work_time) || 0);
                  const skippedToday = isSkippedToday(js);
                  return (
                    <Card
                      key={js.id}
                      draggable={!skippedToday}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      sx={{
                        mb: 1,
                        cursor: skippedToday ? 'default' : 'move',
                        borderLeft: '4px solid',
                        borderLeftColor: skippedToday ? 'warning.main' : 'primary.main',
                        opacity: skippedToday ? 0.72 : (dragIndex === idx ? 0.5 : 1),
                      }}
                      variant="outlined"
                      aria-label={t('routeSetup.stopAria', { index: idx + 1, title })}
                    >
                      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicatorIcon color="action" aria-hidden />
                          <Tooltip title={t('routeSetup.stopNumberTooltip')}>
                            <span>
                              <StopNumberField
                                index={idx}
                                count={slotJobSites.length}
                                onMove={(toIdx) => reorderLocal(idx, toIdx)}
                                ariaLabel={t('routeSetup.stopNumberAria', { title })}
                              />
                            </span>
                          </Tooltip>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {title}
                            </Typography>
                            {subtitle && (
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                {subtitle}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              icon={<HourglassIcon />}
                              color={totalWorkTime > 0 ? 'default' : 'warning'}
                              variant="outlined"
                              label={
                                totalWorkTime > 0
                                  ? t('routeSetup.timeAllocationChip', { time: formatAllocatedMinutes(totalWorkTime) })
                                  : t('routeSetup.timeAllocationNotSet')
                              }
                              aria-label={
                                totalWorkTime > 0
                                  ? t('routeSetup.timeAllocationAria', { time: formatAllocatedMinutes(totalWorkTime) })
                                  : t('routeSetup.timeAllocationNotSet')
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                openTimeAllocation(js);
                              }}
                              clickable
                              sx={{ mt: 0.75 }}
                            />
                            <Tooltip
                              title={js.is_snow_job
                                ? t('routeSetup.snowListStopTooltip')
                                : t('routeSetup.switchToSnowTooltip')}
                            >
                              <Chip
                                size="small"
                                icon={js.is_snow_job ? <SnowIcon /> : <SummerIcon />}
                                color={js.is_snow_job ? 'info' : 'default'}
                                variant="outlined"
                                label={js.is_snow_job
                                  ? t('routeSetup.snowStopChip')
                                  : t('routeSetup.summerStopChip')}
                                aria-label={js.is_snow_job
                                  ? t('routeSetup.snowStopChip')
                                  : t('routeSetup.switchToSnowAria', { title })}
                                // A snow-list stop has no weekday to return
                                // to, so only summer stops switch here.
                                onClick={js.is_snow_job ? undefined : (event) => {
                                  event.stopPropagation();
                                  toggleSnowStop(js);
                                }}
                                disabled={savingSeasonId === js.id}
                                clickable={!js.is_snow_job}
                                sx={{ mt: 0.75, ml: 0.75 }}
                              />
                            </Tooltip>
                            {skippedToday && (
                              <Chip
                                size="small"
                                color="warning"
                                label={t('routeSetup.skippedTodayChip')}
                                sx={{ mt: 0.75, ml: 0.75 }}
                              />
                            )}
                          </Box>
                          <Tooltip title={t('routeSetup.editTimeAllocationTooltip')}>
                            <IconButton
                              size="small"
                              onClick={() => openTimeAllocation(js)}
                              aria-label={t('routeSetup.editTimeAllocationAria', { title })}
                            >
                              <HourglassIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('routeSetup.moveUp')}>
                            <span>
                              <IconButton size="small" onClick={() => moveUp(idx)} disabled={idx === 0} aria-label={t('routeSetup.moveUp')}>
                                <UpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('routeSetup.moveDown')}>
                            <span>
                              <IconButton size="small" onClick={() => moveDown(idx)} disabled={idx === slotJobSites.length - 1} aria-label={t('routeSetup.moveDown')}>
                                <DownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              js.urgent_reminder
                                ? t('routeSetup.editReminderActiveTooltip')
                                : t('routeSetup.editReminderTooltip')
                            }
                          >
                            <IconButton
                              size="small"
                              color={js.urgent_reminder ? 'error' : 'default'}
                              onClick={() => openReminder(js)}
                              aria-label={
                                js.urgent_reminder
                                  ? t('routeSetup.editReminderActiveAria', { title })
                                  : t('routeSetup.editReminderAria', { title })
                              }
                            >
                              {js.urgent_reminder
                                ? <ReminderIcon fontSize="small" />
                                : <ReminderOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('routeSetup.moveToSlot')}>
                            <IconButton size="small" onClick={() => openMove(js)} aria-label={t('routeSetup.moveToSlot')}>
                              <MoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton size="small" color="error" onClick={() => openRemoveStop(js)} aria-label={t('routeSetup.removeStop', { title })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </List>
            )}
          </Grid>

          {/* Map on the right, flush-top aligned with the stops list */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <RouteMap
                stops={mapStops}
                geometry={routeGeometry}
                depot={depotCoords}
                geocoding={geocodingSlot}
                height={480}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeSlot} aria-label={t('common.close')}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ===================================================================
  // EDIT URGENT REMINDER (FB-004)
  // ===================================================================

  const openReminder = (stop) => {
    setReminderTarget(stop);
    setReminderText(stop?.urgent_reminder || '');
    setReminderTextEs(stop?.urgent_reminder_es || '');
    setReminderOpen(true);
  };

  const closeReminder = () => {
    setReminderOpen(false);
    setReminderTarget(null);
    setReminderText('');
    setReminderTextEs('');
    setTranslatingReminder(false);
  };

  const translateReminderToSpanish = async () => {
    const text = reminderText.trim();
    if (!text) return;

    try {
      setTranslatingReminder(true);
      const response = await makeApiCall('/api/translation/to-es-mx', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('routeSetup.failedToTranslateReminder'));
      }

      // TKT-282: write the suggestion into the Spanish-only field, never
      // into reminderText - overwriting the English original was why
      // English-logged-in crews ended up seeing Spanish text.
      setReminderTextEs(data.translatedText || text);
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToTranslateReminder'));
    } finally {
      setTranslatingReminder(false);
    }
  };

  // Naomi, Oct 19 2026: switch a stop between summer and snow. Winter stops
  // give the crew the snow checklist at clock-in/out and winter-only logs;
  // the backend re-reads the allotted time and updates today's open job.
  // TKT-443: a summer stop switched to snow moves to the truck's season snow
  // list, so it leaves this weekday.
  const toggleSnowStop = async (stop) => {
    if (!stop?.id || stop.is_snow_job) return;
    try {
      setSavingSeasonId(stop.id);
      const response = await makeApiCall(`/api/routes/job-sites/${stop.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_snow_job: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('routeSetup.failedToSwitchSeason'));
      await loadSlot(selectedDay, selectedTruckId);
      await loadAllSlotCounts();
      loadCurrentRouteGeometry(selectedDay, selectedTruckId);
      setSuccessMsg(t('routeSetup.movedToSnowList'));
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToSwitchSeason'));
    } finally {
      setSavingSeasonId(null);
    }
  };

  const saveReminder = async () => {
    if (!reminderTarget?.id) return;
    try {
      setSavingReminder(true);
      const response = await makeApiCall(`/api/routes/job-sites/${reminderTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          urgent_reminder: reminderText,
          urgent_reminder_es: reminderTextEs,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('routeSetup.failedToSaveReminder'));
      }
      const data = await response.json().catch(() => ({}));
      const updated = data?.job_site;
      if (updated) {
        setSlotJobSites((prev) =>
          prev.map((js) => (js.id === updated.id ? { ...js, ...updated } : js))
        );
      } else {
        await loadSlot(selectedDay, selectedTruckId);
      }
      setSuccessMsg(t('routeSetup.reminderSaved'));
      closeReminder();
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToSaveReminder'));
    } finally {
      setSavingReminder(false);
    }
  };

  // ===================================================================
  // EDIT TIME ALLOCATION (FB-005)
  // ===================================================================

  const openTimeAllocation = (stop) => {
    setTimeTarget(stop);
    setTimeOpen(true);
  };

  const closeTimeAllocation = () => {
    setTimeOpen(false);
    setTimeTarget(null);
  };

  const openTimeAllocationQuote = () => {
    if (!timeTarget?.quote_id) return;
    closeTimeAllocation();
    navigate(`/dashboard/quotes?quoteId=${encodeURIComponent(timeTarget.quote_id)}&action=view`);
  };

  const saveTimeAllocation = async () => {
    if (!timeTarget?.id) return;
    try {
      setSavingTime(true);
      const response = await makeApiCall(`/api/routes/job-sites/${timeTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          sync_from_quote: true,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('routeSetup.failedToSaveTimeAllocation'));
      }
      const data = await response.json().catch(() => ({}));
      const updated = data?.job_site;
      if (updated) {
        setSlotJobSites((prev) =>
          prev.map((js) => (js.id === updated.id ? { ...js, ...updated } : js))
        );
      } else {
        await loadSlot(selectedDay, selectedTruckId);
      }
      setSuccessMsg(t('routeSetup.timeAllocationSaved'));
      closeTimeAllocation();
    } catch (err) {
      console.error(err);
      setError(err.message || t('routeSetup.failedToSaveTimeAllocation'));
    } finally {
      setSavingTime(false);
    }
  };

  // ===================================================================
  // ADD CUSTOMERS DIALOG
  // ===================================================================

  const renderAddCustomersDialog = () => (
    <Dialog open={addCustomersOpen} onClose={() => setAddCustomersOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>
        {t('routeSetup.addCustomersTitle')}
        {selectedTruck && selectedDayLabel && (
          <Typography variant="caption" color="text.secondary" display="block">
            {selectedDayLabel} · {selectedTruck.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder={t('routeSetup.searchCustomers')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': t('routeSetup.searchCustomers') }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="service-type-filter-label">{t('routeSetup.serviceType')}</InputLabel>
            <Select
              labelId="service-type-filter-label"
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              label={t('routeSetup.serviceType')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="lawn">{t('routeSetup.lawn')}</MenuItem>
              <MenuItem value="gardening">{t('routeSetup.gardening')}</MenuItem>
              <MenuItem value="snow">{t('routeSetup.snow')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Typography variant="body2" id="stop-season-label" sx={{ fontWeight: 600 }}>
            {t('routeSetup.stopSeasonLabel')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={stopSeason || null}
            onChange={(e, value) => { if (value) setStopSeason(value); }}
            aria-labelledby="stop-season-label"
          >
            <ToggleButton value="summer" color="success" disabled={isSnowListSlot}>
              <SummerIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t('routeSetup.stopSeasonSummer')}
            </ToggleButton>
            <ToggleButton value="snow" color="info" disabled={!isSnowListSlot}>
              <SnowIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t('routeSetup.stopSeasonSnow')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {!stopSeason && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t('routeSetup.chooseSeasonFirst')}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          {isSnowListSlot ? t('routeSetup.snowListAddHint') : t('routeSetup.weekdayAddHint')}
        </Typography>

        {availableLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableCustomers.length === 0 ? (
          <Alert severity="info">{t('routeSetup.noMatchingCustomers')}</Alert>
        ) : (
          <List dense>
            {availableCustomers.map((c) => {
              const label = c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
              const addr = [c.street_address, c.city].filter(Boolean).join(', ');
              const quotes = Array.isArray(c.available_quotes) ? c.available_quotes : [];
              const singleQuote = quotes.length === 1 ? quotes[0] : null;
              const buttonLabel = quotes.length > 1
                ? t('routeSetup.pickQuote', { count: quotes.length })
                : t(isSnow ? 'routeSetup.addSnowStop' : 'routeSetup.addSummerStop');
              return (
                <ListItem
                  key={c.id}
                  divider
                  secondaryAction={
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => addCustomerToSlot(c)}
                      disabled={!stopSeason || !!adding[c.id] || quotes.length === 0}
                      startIcon={adding[c.id] ? <CircularProgress size={14} /> : <AddIcon />}
                      aria-label={t('routeSetup.addCustomerAria', { name: label })}
                    >
                      {buttonLabel}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={label}
                    secondary={
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {addr}
                        {singleQuote?.quote_number && (
                          <Chip
                            label={singleQuote.quote_number}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {quotes.length > 1 && (
                          <Chip
                            label={t('routeSetup.quotesAvailable', { count: quotes.length })}
                            size="small"
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddCustomersOpen(false)}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );

  // ===================================================================
  // QUOTE PICKER DIALOG
  // Opens when a customer has multiple approved quotes not yet on a route,
  // so the admin can pick which contract this stop is fulfilling.
  // ===================================================================

  const renderQuotePickerDialog = () => {
    const customer = quotePickerCustomer;
    const label = customer
      ? (customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim())
      : '';
    const quotes = customer && Array.isArray(customer.available_quotes)
      ? customer.available_quotes
      : [];
    return (
      <Dialog
        open={quotePickerOpen}
        onClose={() => {
          if (quotePickerLoadingId) return;
          setQuotePickerOpen(false);
          setQuotePickerCustomer(null);
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="route-quote-picker-title"
      >
        <DialogTitle id="route-quote-picker-title">
          {t('routeSetup.pickQuoteTitle')}
          {label && (
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('routeSetup.pickQuoteHelper')}
          </Typography>
          <List dense>
            {quotes.map((q) => {
              const total = q.total_amount != null
                ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'CAD' }).format(Number(q.total_amount))
                : null;
              const isLoading = quotePickerLoadingId === q.id;
              return (
                <ListItem
                  key={q.id}
                  divider
                  secondaryAction={
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => confirmQuotePick(q)}
                      disabled={!stopSeason || !!quotePickerLoadingId}
                      startIcon={isLoading ? <CircularProgress size={14} /> : <AddIcon />}
                      aria-label={t('routeSetup.addQuoteAria', { number: q.quote_number || q.id })}
                    >
                      {t(isSnow ? 'routeSetup.addSnowStop' : 'routeSetup.addSummerStop')}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {q.quote_number || `#${q.id}`}
                        </Typography>
                        {q.service_type && (
                          <Chip label={q.service_type} size="small" />
                        )}
                        {total && (
                          <Chip label={total} size="small" variant="outlined" />
                        )}
                      </Stack>
                    }
                    secondary={
                      q.created_at
                        ? new Date(q.created_at).toLocaleDateString()
                        : null
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQuotePickerOpen(false);
              setQuotePickerCustomer(null);
            }}
            disabled={!!quotePickerLoadingId}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ===================================================================
  // OPTIMIZE DIALOG
  // ===================================================================

  const formatMinutes = (seconds) => {
    if (!seconds && seconds !== 0) return '\u2014';
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const rest = mins % 60;
    return `${hrs}h ${rest}m`;
  };

  const formatDistance = (meters) => {
    if (!meters && meters !== 0) return '\u2014';
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const renderCrewDialog = () => {
    const renderEmployeeName = (emp) => {
      const full = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
      return full || emp.username || `#${emp.id}`;
    };
    return (
      <Dialog
        open={crewOpen}
        onClose={() => setCrewOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="crew-dialog-title"
      >
        <DialogTitle id="crew-dialog-title">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CrewIcon color="primary" />
            <span>
              {t('routeSetup.assignCrew')} — {selectedTruck?.name || ''}
            </span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('routeSetup.crewTruckLevelWarning')}
          </Alert>

          {loadingEmployees && <LinearProgress sx={{ mb: 2 }} />}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="crew-driver-label">{t('routeSetup.driver')}</InputLabel>
            <Select
              labelId="crew-driver-label"
              value={crewDriverId}
              label={t('routeSetup.driver')}
              onChange={(e) => setCrewDriverId(e.target.value)}
              inputProps={{ 'aria-label': t('routeSetup.driver') }}
            >
              <MenuItem value="">
                <em>{t('routeSetup.noDriver')}</em>
              </MenuItem>
              {availableEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {renderEmployeeName(emp)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="crew-passenger-label">{t('routeSetup.passenger')}</InputLabel>
            <Select
              labelId="crew-passenger-label"
              value={crewPassengerId}
              label={t('routeSetup.passenger')}
              onChange={(e) => setCrewPassengerId(e.target.value)}
              inputProps={{ 'aria-label': t('routeSetup.passenger') }}
            >
              <MenuItem value="">
                <em>{t('routeSetup.noPassenger')}</em>
              </MenuItem>
              {availableEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {renderEmployeeName(emp)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AdditionalCrewField
            employees={availableEmployees}
            value={crewExtras}
            onChange={setCrewExtras}
            driverId={crewDriverId}
            passengerId={crewPassengerId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrewOpen(false)} disabled={savingCrew}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={saveCrew}
            disabled={savingCrew}
            startIcon={savingCrew ? <CircularProgress size={16} /> : null}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderMoveDialog = () => {
    const moveTargetTitle = moveTarget
      ? (moveTarget.company_name
          || `${moveTarget.first_name || ''} ${moveTarget.last_name || ''}`.trim()
          || t('routeSetup.unknownCustomer'))
      : '';
    return (
      <Dialog open={moveOpen} onClose={closeMove} fullWidth maxWidth="xs">
        <DialogTitle>{t('routeSetup.moveToSlot')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('routeSetup.moveHelper', { customer: moveTargetTitle })}
          </Typography>
          <Stack spacing={2}>
            {/* A snow stop can only move to another truck's snow list. */}
            {moveTarget?.is_snow_job ? (
              <Typography variant="body2" color="text.secondary">
                {t('routeSetup.moveSnowListHelper')}
              </Typography>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel id="move-day-label">{t('routeSetup.day')}</InputLabel>
                <Select
                  labelId="move-day-label"
                  label={t('routeSetup.day')}
                  value={moveDay ?? ''}
                  onChange={(e) => setMoveDay(parseInt(e.target.value, 10))}
                  inputProps={{ 'aria-label': t('routeSetup.day') }}
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {t(`days.${d.key}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth size="small">
              <InputLabel id="move-truck-label">{t('routeSetup.truck')}</InputLabel>
              <Select
                labelId="move-truck-label"
                label={t('routeSetup.truck')}
                value={moveTruckId ?? ''}
                onChange={(e) => setMoveTruckId(parseInt(e.target.value, 10))}
                inputProps={{ 'aria-label': t('routeSetup.truck') }}
              >
                {trucks.map((tr) => (
                  <MenuItem key={tr.id} value={tr.id}>
                    {tr.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMove}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={applyMove}
            disabled={moving || moveDay == null || !moveTruckId}
            startIcon={moving ? <CircularProgress size={14} /> : <MoveIcon />}
            aria-label={t('routeSetup.moveToSlot')}
          >
            {t('routeSetup.moveToSlot')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderReminderDialog = () => {
    const reminderTitle = reminderTarget
      ? (reminderTarget.company_name
          || `${reminderTarget.first_name || ''} ${reminderTarget.last_name || ''}`.trim()
          || t('routeSetup.unknownCustomer'))
      : '';
    const isClearing = (reminderTarget?.urgent_reminder || '') !== ''
                    && reminderText.trim() === '';
    return (
      <Dialog open={reminderOpen} onClose={closeReminder} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('routeSetup.editReminderTitle')}
          {reminderTitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {reminderTitle}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('routeSetup.editReminderHelper')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label={t('routeSetup.editReminderEnglishLabel')}
            value={reminderText}
            onChange={(e) => setReminderText(e.target.value)}
            placeholder={t('routeSetup.editReminderPlaceholder')}
            inputProps={{
              'aria-label': t('routeSetup.editReminderEnglishLabel'),
              maxLength: 1000,
            }}
          />
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={translatingReminder ? <CircularProgress size={14} /> : <TranslateIcon />}
              onClick={translateReminderToSpanish}
              disabled={translatingReminder || reminderText.trim() === ''}
              aria-label={t('routeSetup.translateReminderToSpanish')}
            >
              {t('routeSetup.translateReminderToSpanish')}
            </Button>
          </Box>
          {/* TKT-282: the Spanish text lives in its own field so it never
              clobbers the English original. Crews see this version only
              when their account language is Spanish; English is always
              the fallback. */}
          <TextField
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
            label={t('routeSetup.editReminderSpanishLabel')}
            value={reminderTextEs}
            onChange={(e) => setReminderTextEs(e.target.value)}
            placeholder={t('routeSetup.editReminderSpanishPlaceholder')}
            inputProps={{
              'aria-label': t('routeSetup.editReminderSpanishLabel'),
              maxLength: 1000,
            }}
          />
          {isClearing && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('routeSetup.editReminderClearHint')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReminder}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color={isClearing ? 'inherit' : 'error'}
            onClick={saveReminder}
            disabled={savingReminder}
            startIcon={savingReminder ? <CircularProgress size={14} /> : <ReminderIcon />}
            aria-label={isClearing
              ? t('routeSetup.editReminderClear')
              : t('common.save')}
          >
            {isClearing ? t('routeSetup.editReminderClear') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderTimeAllocationDialog = () => {
    const timeTitle = timeTarget
      ? (timeTarget.company_name
          || `${timeTarget.first_name || ''} ${timeTarget.last_name || ''}`.trim()
          || t('routeSetup.unknownCustomer'))
      : '';
    const totalMins = Number(timeTarget?.allocated_minutes || 0)
      || (Number(timeTarget?.lawn_work_time) || 0) + (Number(timeTarget?.gardening_work_time) || 0);
    const breakdown = Array.isArray(timeTarget?.allocation_breakdown)
      ? timeTarget.allocation_breakdown
      : [];
    const breakdownTotal = breakdown.reduce((sum, item) => sum + Number(item.minutes || 0), 0);

    return (
      <Dialog open={timeOpen} onClose={closeTimeAllocation} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('routeSetup.editTimeAllocationTitle')}
          {timeTitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {timeTitle}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('routeSetup.editTimeAllocationHelper')}
          </Typography>
          <Alert severity={totalMins > 0 ? 'info' : 'warning'} sx={{ mt: 2 }}>
            {totalMins > 0
              ? t('routeSetup.timeAllocationTotal', { time: formatAllocatedMinutes(totalMins) })
              : t('routeSetup.timeAllocationTotalNotSet')}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('routeSetup.timeAllocationBreakdownTitle')}
            </Typography>
            {breakdown.length > 0 ? (
              <Stack spacing={1}>
                {breakdown.map((item, index) => (
                  <Box
                    key={`${item.key || item.service_type}-${index}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {getAllocationBreakdownLabel(item)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatAllocatedMinutes(item.minutes)}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('routeSetup.timeAllocationBreakdownTotal')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {formatAllocatedMinutes(breakdownTotal)}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Alert severity="warning">
                {t('routeSetup.timeAllocationBreakdownEmpty')}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTimeAllocation}>{t('common.cancel')}</Button>
          <Button
            variant="outlined"
            onClick={openTimeAllocationQuote}
            disabled={!timeTarget?.quote_id}
            aria-label={t('routeSetup.openQuoteAria')}
          >
            {t('routeSetup.openQuote')}
          </Button>
          <Button
            variant="contained"
            onClick={saveTimeAllocation}
            disabled={savingTime}
            startIcon={savingTime ? <CircularProgress size={14} /> : <HourglassIcon />}
            aria-label={t('routeSetup.syncTimeAllocation')}
          >
            {t('routeSetup.syncTimeAllocation')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderOptimizeDialog = () => {
    const order = optimizePreview?.optimized_order || [];
    const orderedCustomers = order
      .map((id) => slotJobSites.find((js) => js.id === id))
      .filter(Boolean);

    return (
      <Dialog open={optimizeOpen} onClose={() => setOptimizeOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('routeSetup.optimizeTitle')}</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={depotAddress}
              onChange={(e) => setDepotAddress(e.target.value)}
              label={t('routeSetup.depotAddressOptional')}
              placeholder={t('routeSetup.depotPlaceholder')}
              helperText={t('routeSetup.depotHelper')}
              inputProps={{ 'aria-label': t('routeSetup.depotAddressOptional') }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={avoidTolls}
                  onChange={(e) => setAvoidTolls(e.target.checked)}
                  inputProps={{ 'aria-label': t('routeSetup.avoidTolls') }}
                />
              }
              label={t('routeSetup.avoidTolls')}
            />
          </Stack>

          {optimizing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!optimizing && optimizePreview && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('routeSetup.distance')}
                  </Typography>
                  <Typography variant="h6">{formatDistance(optimizePreview.distance)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('routeSetup.duration')}
                  </Typography>
                  <Typography variant="h6">{formatMinutes(optimizePreview.duration)}</Typography>
                </Grid>
                {optimizePreview.savings && (
                  <>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('routeSetup.distanceSaved')}
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatDistance(optimizePreview.savings.distanceSavedMeters)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('routeSetup.timeSaved')}
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatMinutes(optimizePreview.savings.timeSavedSeconds)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              {optimizePreview.algorithm === 'extended' && (
                <Alert severity="info" sx={{ mb: 2 }} role="status" aria-live="polite">
                  {t('routeSetup.optimizeExtendedNote', { count: optimizePreview.stop_count })}
                </Alert>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('routeSetup.proposedOrder')}
              </Typography>
              <List dense>
                {orderedCustomers.map((js, idx) => {
                  const label = js.company_name || `${js.first_name || ''} ${js.last_name || ''}`.trim();
                  return (
                    <ListItem key={js.id} divider>
                      <ListItemText
                        primary={`${idx + 1}. ${label}`}
                        secondary={[js.street_address, js.city].filter(Boolean).join(', ')}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {!optimizing && !optimizePreview && (
            <Alert severity="info">{t('routeSetup.optimizePrompt')}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptimizeOpen(false)}>{t('common.cancel')}</Button>
          {!optimizePreview ? (
            <Button
              variant="contained"
              onClick={runOptimize}
              disabled={optimizing}
              startIcon={optimizing ? <CircularProgress size={14} /> : <OptimizeIcon />}
              aria-label={t('routeSetup.runOptimize')}
            >
              {t('routeSetup.runOptimize')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={applyOptimize}
              disabled={applyingOptimize}
              startIcon={applyingOptimize ? <CircularProgress size={14} /> : null}
              aria-label={t('routeSetup.applyOptimize')}
            >
              {t('routeSetup.applyOptimize')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // ===================================================================
  // RENDER
  // ===================================================================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-demo-target="page-route-setup-root">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <RouteIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('routeSetup.pageTitle')}
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
        {t('routeSetup.intro')}
      </Alert>

      {renderGrid()}
      {!slotOpen && (
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
          <RouteIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('routeSetup.chooseSlotPrompt')}
          </Typography>
        </Paper>
      )}
      {renderSlotDialog()}
      {renderAddCustomersDialog()}
      {renderQuotePickerDialog()}
      {renderCrewDialog()}
      {renderOptimizeDialog()}
      {renderMoveDialog()}
      {renderReminderDialog()}
      {renderTimeAllocationDialog()}
      {renderRemoveStopDialog()}

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3500}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RouteSetup;
